import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useSelector } from "react-redux";
import { RootStore } from "../../state/store";
import { VerseType } from "../../types/practiceTypes";
import { shuffle } from "../../util/generalUtil";

type Phases = "BEFOREHAND" | "READY" | "MEMORIZING" | "CONTINUE" | "FINISHED";
interface MemorizeWord {
  word: string;
  status: "GIVEN" | "C" | "I" | "HIDDEN";
}

export function Memorize() {
  useDocumentTitle("Memorize - Bible Quiz Academy");
  const material = useSelector((state: RootStore) => state.material.material);
  const settings = useSelector((state: RootStore) => state.settings);
  const chapters = settings.selectedChapters;

  const [verseList, setVerseList] = useState<VerseType[]>([]);
  const verseListRef = useRef(verseList);
  const [currentVerse, setCurrentVerse] = useState(verseList[0]);
  const [phase, setPhase] = useState<Phases>("BEFOREHAND");
  const phaseRef = useRef<Phases>(phase);
  const versePhase = useRef(0); // percentage of words that are hidden
  const [words, setWords] = useState<MemorizeWord[]>([]);
  const stats = useRef<{ totalVerses: number; versesComplete: number }>({
    totalVerses: 0,
    versesComplete: 0,
  });

  useEffect(() => {
    if (material.length === 0) return;
    const allVerses: VerseType[] = [];
    material.forEach((book, index) => {
      book.chapters.forEach((chapter, chapterIndex) => {
        if (chapters && chapters[index] && chapters[index][chapterIndex]) {
          chapter.forEach((verse, verseIndex) => {
            const chapterFromSettings = chapters[index][chapterIndex];
            const chapterSettings =
              typeof chapterFromSettings === "boolean"
                ? Array(chapter.length).fill(chapterFromSettings)
                : chapterFromSettings;
            if (chapterSettings[verseIndex]) {
              allVerses.push({
                book: index,
                chapter: chapterIndex,
                verseNum: verseIndex,
                reference: `${book.bookName} ${chapterIndex + 1}:${
                  verseIndex + 1
                }`,
                ...verse,
              });
            }
          });
        }
      });
    });
    setVerseList(allVerses);
    verseListRef.current = allVerses;
    setPhase("READY");
    phaseRef.current = "READY";
    stats.current.totalVerses = allVerses.length;
  }, [material]);
  useEffect(() => {
    if (phase === "MEMORIZING") {
      const el = document.getElementById("mem-text-area");
      if (el) {
        el.focus();
        // @ts-ignore
        el.selectionStart = el.value?.length || 0;
      }
    }
  }, [phase]);

  const characterHandler = (code: string) => {
    if (code === "Space") {
      switch (phaseRef.current) {
        case "READY":
          clickNextVerse();
          break;
        case "CONTINUE":
          clickContinue();
          break;
        default:
          break;
      }
      return;
    }

    if (phaseRef.current === "MEMORIZING") {
      const keyPressed = code.substring(3, 4).toLowerCase();
      if (keyPressed.match(/[a-z0-9]/i)) {
        setWords((words) => {
          const newWords = [...words];
          const index = newWords.findIndex((w) => w.status === "HIDDEN");
          if (index === -1) return words;
          const firstLetter = newWords[index].word
            .toLowerCase()
            .match(/[a-z0-9]/)
            ?.pop();
          newWords[index] = {
            ...newWords[index],
            status: firstLetter && firstLetter === keyPressed ? "C" : "I",
          };
          if (!newWords.some((w) => w.status === "HIDDEN")) {
            const allCorrect = !newWords.some((w) => w.status !== "C");
            if (allCorrect) {
              stats.current.versesComplete += 1;
              const newPhase: Phases =
                stats.current.totalVerses === stats.current.versesComplete
                  ? "FINISHED"
                  : "READY";

              setVerseList((list) => {
                const newVerses = [...list];
                newVerses.splice(0, 1);
                verseListRef.current = newVerses;
                return newVerses;
              });

              setPhase(newPhase);
              phaseRef.current = newPhase;
            } else {
              setPhase("CONTINUE");
              phaseRef.current = "CONTINUE";
            }
          }
          return newWords;
        });
      }
    }
  };
  const upHandler = (e: KeyboardEvent) => {
    characterHandler(e.code);
  };

  useEffect(() => {
    if (!settings.touchscreen) {
      window.addEventListener("keyup", upHandler);
      // Remove event listeners on cleanup
      return () => {
        window.removeEventListener("keyup", upHandler);
      };
    }
  }, []);

  const hideWords = (
    versePhase: number,
    words: MemorizeWord[]
  ): MemorizeWord[] => {
    const newWords: MemorizeWord[] = words.map((w) => ({
      ...w,
      status: "GIVEN",
    }));
    if (versePhase >= 100) {
      // Hide all words
      newWords.forEach((w) => (w.status = "HIDDEN"));
    } else if (versePhase > 0) {
      // Hide some words
      let chunkSize = Math.ceil(versePhase / 25);
      const numberOfChunks = Math.ceil(
        (newWords.length * (versePhase / 100)) / chunkSize
      );
      for (let i = 0; i < numberOfChunks; i++) {
        const possibleIndicies: number[] = [];
        for (let wordIndex = 0; wordIndex < newWords.length; wordIndex++) {
          const w = newWords[wordIndex];
          if (w.status === "GIVEN") {
            let taken = false;
            for (let j = wordIndex - 1; j < wordIndex + chunkSize + 1; j++) {
              if (j - 1 >= newWords.length) {
                taken = true;
                break;
              }
              if (newWords[j] && newWords[j].status !== "GIVEN") taken = true;
            }
            if (!taken) possibleIndicies.push(wordIndex);
          }
        }
        if (possibleIndicies.length > 0) {
          shuffle(possibleIndicies);
          const startIndex = possibleIndicies[0];
          for (let j = startIndex; j < startIndex + chunkSize; j++) {
            newWords[j].status = "HIDDEN";
          }
        } else {
          // Keep trying with smaller chunk sizes
          if (chunkSize > 1) {
            chunkSize--;
            i--;
          }
        }
      }
    }
    return newWords;
  };

  const clickContinue = () => {
    setPhase("MEMORIZING");
    phaseRef.current = "MEMORIZING";
    setWords((words) => {
      const allCorrect = !words.some((w) => w.status === "I");
      if (allCorrect) versePhase.current += 15;
      return hideWords(versePhase.current, words);
    });
  };
  const clickNextVerse = () => {
    setPhase("CONTINUE");
    phaseRef.current = "CONTINUE";
    setCurrentVerse(verseListRef.current[0]);
    const allWords: MemorizeWord[] = verseListRef.current[0].verse
      .split(" ")
      .map((w) => ({ word: w, status: "GIVEN" }));
    versePhase.current = 0;
    setWords(hideWords(0, allWords));
  };

  return (
    <div
      className="page"
      style={{
        padding: 20,
        minHeight: "calc(100vh - 20px)",
        minWidth: 300,
      }}
    >
      <div style={{ height: 150, marginTop: 10 }}>
        <div>
          {(phase === "MEMORIZING" ||
            phase === "CONTINUE" ||
            (phase === "READY" && stats.current.versesComplete > 0)) && (
            <>
              <span>{currentVerse.reference}</span>
              <br />
              <br />
              <span>
                {words.map((w, i) => (
                  <React.Fragment key={i}>
                    <span
                      style={{
                        color: w.status === "I" ? "red" : undefined,
                        textDecoration: w.status === "C" ? "underline" : "",
                      }}
                    >
                      {w.status === "HIDDEN" ? "_____" : w.word}
                    </span>
                    <span> </span>
                  </React.Fragment>
                ))}
              </span>
            </>
          )}
          {phase === "READY" && stats.current.versesComplete > 0 && (
            <>
              <br />
              <br />
              <span>Verse memorized!</span>
            </>
          )}
          {phase === "FINISHED" && (
            <>
              <span>Memorization complete!</span>
              <br />
              <br />
              <span>
                You can now review your verses in{" "}
                <Link className="link" to="/versecards">
                  Verse Cards
                </Link>
              </span>
            </>
          )}
        </div>
      </div>
      {phase === "MEMORIZING" &&
        (settings.touchscreen ? (
          <textarea
            id="mem-text-area"
            value="Type the first letter of each blank word. Click here for the digital keyboard."
            style={{
              backgroundColor: "transparent",
              border: "none",
              minHeight: 96,
              color: "inherit",
              width: "100%",
              outline: "none",
              font: "inherit",
              display: "inline-block",
              resize: "none",
              overflow: "hidden",
            }}
            onInput={(e) => {
              const chars = (e.nativeEvent as InputEvent).data || "";
              const key = chars.substring(chars.length - 1, chars.length);
              characterHandler(`Key${key}`);
              e.preventDefault();
              e.stopPropagation();
            }}
          />
        ) : (
          <p>Type the first letter of each blank word.</p>
        ))}
      {phase === "CONTINUE" && (
        <button className="clickable" key="continue" onClick={clickContinue}>
          Continue
        </button>
      )}
      {phase === "READY" && verseList.length > 0 && (
        <button className="clickable" key="next" onClick={clickNextVerse}>
          Next Verse
        </button>
      )}
      <div
        style={{
          visibility:
            phase === "READY" || phase === "FINISHED" ? "visible" : "hidden",
          marginTop: 20,
        }}
      >
        <div>{`Total Verses: ${stats.current.totalVerses}`}</div>
        <div>{`Verses Complete: ${stats.current.versesComplete}`}</div>
      </div>
      <div style={{ marginTop: 20 }}>
        <Link className="back-button" to="/">
          Go Back
        </Link>
      </div>
    </div>
  );
}
